import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export const PWA_INSTALL_DISMISSED_KEY = 'civicode:pwa-install-dismissed:v1';

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return Boolean(
    window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator?.standalone === true
  );
}

function detectPlatform() {
  if (typeof navigator === 'undefined') return { isIos: false, isSafari: false };
  const ua = navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(ua)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|Chrome|Chromium|Edg\//i.test(ua);
  return { isIos, isSafari };
}

function readDismissed() {
  try {
    const value = window.localStorage.getItem(PWA_INSTALL_DISMISSED_KEY);
    if (value === 'true') return true;

    // Timestamps were written by the previous 30-day dismissal behavior.
    const legacyDismissedAt = Number(value);
    return Number.isFinite(legacyDismissedAt) && legacyDismissedAt > 0;
  } catch {
    return false;
  }
}

const PwaInstallContext = createContext(null);

export function PwaInstallProvider({ children }) {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dismissed, setDismissed] = useState(readDismissed);
  const platform = useMemo(detectPlatform, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };
    const handleInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
      setDialogOpen(false);
    };
    const media = window.matchMedia?.('(display-mode: standalone)');
    const handleDisplayModeChange = (event) => {
      if (event.matches) handleInstalled();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);
    media?.addEventListener?.('change', handleDisplayModeChange);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
      media?.removeEventListener?.('change', handleDisplayModeChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!installPrompt) return null;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    setInstallPrompt(null);
    if (choice?.outcome === 'accepted') setDialogOpen(false);
    return choice?.outcome || null;
  }, [installPrompt]);

  const dismissCard = useCallback(() => {
    try {
      window.localStorage.setItem(PWA_INSTALL_DISMISSED_KEY, 'true');
    } catch {
      // The invitation remains dismissible for this session when storage is unavailable.
    }
    setDismissed(true);
  }, []);

  const value = useMemo(() => ({
    installed,
    canPrompt: Boolean(installPrompt),
    isIos: platform.isIos,
    isIosSafari: platform.isIos && platform.isSafari,
    dialogOpen,
    openInstallDialog: () => setDialogOpen(true),
    closeInstallDialog: () => setDialogOpen(false),
    promptInstall,
    dismissCard,
    showInstallCard: !installed && !dismissed,
  }), [dialogOpen, dismissed, installPrompt, installed, platform, promptInstall, dismissCard]);

  return <PwaInstallContext.Provider value={value}>{children}</PwaInstallContext.Provider>;
}

export function usePwaInstall() {
  const context = useContext(PwaInstallContext);
  if (!context) throw new Error('usePwaInstall must be used within PwaInstallProvider');
  return context;
}
